import { classifyArtifactFile } from "./framework/artifacts.js";
export const PROJECT_DOCUMENT_DELETE_MAX_PER_CALL = 25;
/**
 * How many documents one bulk reprocess may name.
 *
 * An order of magnitude above the delete cap: a reprocess is a *filter* before it is an action —
 * most of a "reprocess everything" selection is already current and costs one status resolution
 * each — while a delete acts on every id it is given. Still a cap, because each id that survives
 * the filter mints a fresh job set inside the same transaction.
 */
export const PROJECT_DOCUMENT_REPROCESS_MAX_PER_CALL = 200;
/**
 * Pages one chunk of a large-lane read converts, by default.
 *
 * Here rather than only in the pipeline's own config because the Documents screen multiplies it
 * where a run's page ranges are unknown (`chunks.ranges` absent): "About 420 of 3,000 pages" is
 * `chunks.completed × this`. The backend's `CHUNK_PAGES_DEFAULT` is this constant, so the two
 * cannot drift into a read-out that counts pages the reader never converted. A deployment that
 * overrides `DOC_PROCESSING_CHUNK_PAGES` moves the real number without moving this one — which
 * is why that fallback says "About".
 */
export const DOC_PROCESSING_CHUNK_PAGES_DEFAULT = 60;
/**
 * How many chunk steps of a large-lane read may run at once, by default, and how long a read
 * that yielded the shared reader waits before asking for it again.
 *
 * Here for the same reason as the chunk size above: the model-facing wait estimate
 * (`artifactProcessingLib/processingEstimate.ts`) runs inside the query behind every document
 * tool, where the pipeline's full config module is weight the per-turn hot path cannot carry.
 * The backend's `CHUNK_MAX_PARALLELISM_DEFAULT` and `YIELD_DELAY_MS_DEFAULT` are these
 * constants, so an estimate and the lane it estimates cannot disagree about the defaults. A
 * deployment that overrides either env var moves the real number without moving these — which is
 * why the estimate calls itself "typical".
 */
export const DOC_PROCESSING_CHUNK_MAX_PARALLELISM_DEFAULT = 2;
export const DOC_PROCESSING_YIELD_DELAY_MS_DEFAULT = 120_000;
/**
 * The bounds the two chunk knobs are read under, and the reading itself.
 *
 * Beside the defaults because two modules have to agree and neither may import the other: the
 * pipeline's `processingConfig.ts` configures the real lane, and the model-facing estimate runs
 * in the per-turn hot path. A deployment that re-tunes `DOC_PROCESSING_CHUNK_PAGES` moves the
 * parts a read is planned in; an estimate still dividing by the default would quote the wrong
 * number of parts for every file on that deployment.
 */
export const DOC_PROCESSING_CHUNK_PAGES_MIN = 1;
export const DOC_PROCESSING_CHUNK_PAGES_MAX = 90;
export const DOC_PROCESSING_CHUNK_MAX_PARALLELISM_MIN = 1;
export const DOC_PROCESSING_CHUNK_MAX_PARALLELISM_MAX = 16;
function resolveBoundedInteger(value, bounds) {
    if (!value?.trim())
        return bounds.fallback;
    const parsed = Number(value);
    if (!Number.isFinite(parsed))
        return bounds.fallback;
    return Math.max(bounds.min, Math.min(bounds.max, Math.floor(parsed)));
}
/** `DOC_PROCESSING_CHUNK_PAGES` → pages per chunk request. */
export function resolveDocProcessingChunkPages(value) {
    return resolveBoundedInteger(value, {
        fallback: DOC_PROCESSING_CHUNK_PAGES_DEFAULT,
        min: DOC_PROCESSING_CHUNK_PAGES_MIN,
        max: DOC_PROCESSING_CHUNK_PAGES_MAX,
    });
}
/** `DOC_PROCESSING_CHUNK_MAX_PARALLELISM` → how many chunk steps may convert at once. */
export function resolveDocProcessingChunkMaxParallelism(value) {
    return resolveBoundedInteger(value, {
        fallback: DOC_PROCESSING_CHUNK_MAX_PARALLELISM_DEFAULT,
        min: DOC_PROCESSING_CHUNK_MAX_PARALLELISM_MIN,
        max: DOC_PROCESSING_CHUNK_MAX_PARALLELISM_MAX,
    });
}
/**
 * Whether a person's Retry is honest for this status — the answer the button and
 * `retryDocumentProcessing` both read, so the two cannot disagree.
 *
 * A never-processed file is always worth one. So is a stopped one, unconditionally: a stop
 * leaves no job for the pipeline to resume, so Try again is the only way back. A failed file is
 * worth a retry unless the taxonomy classified its failure as deterministic — the same bytes
 * through the same parser fail the same way. An unclassified failure keeps Retry, the person's
 * only self-service move for a code nobody has an opinion on yet.
 */
export function isDocumentRetryOffered(status) {
    if (status.kind === "not_processed" || status.kind === "cancelled")
        return true;
    return status.kind === "failed" && (status.failure?.retryable ?? true);
}
/**
 * Whether a *row* should offer Try again.
 *
 * Only the class rule is extra: an image has no reading lane at all, so its row is permanently
 * `not_processed` and a Retry there queues nothing. The button is withheld rather than disabled
 * because there is nothing to wait for or repair. Every other class — a recording included — is
 * read by a lane whose statuses and failures the status alone already answers for.
 */
export function isDocumentRetryOfferedForFile(status, file) {
    if (classifyArtifactFile(file.fileName, file.mimeType ?? undefined) === "image")
        return false;
    return isDocumentRetryOffered(status);
}
/**
 * What a spreadsheet read writes down and keeps beside the bytes it read.
 *
 * `profile` is every region's full statistics for one sheet, kept out of the region card because
 * a card is a database document and a wide region's profile is megabytes of it; a card over the
 * worker's own bound says so and names the artefact that holds the rest.
 *
 * Order is the schema fingerprint: the Convex table's `kind` union and the worker's own literal
 * list are read against this, so a kind may be added to the end and none may leave.
 */
export const SPREADSHEET_DERIVED_ARTIFACT_KINDS = [
    "census",
    "rowIndex",
    "sharedStrings",
    "profile",
    "embeddedInventory",
];
/**
 * Where a worker may put the profile of the sheet it is reading, one slot per part.
 *
 * Part 0 is the index — the region-level statistics, the headers, and which part holds each
 * column's entry — and parts 1 and up hold the column entries that did not fit it. Every slot is
 * minted before the call and a worker that needs fewer leaves the rest unused: an unredeemed
 * upload URL is a signature, not an allocation.
 */
export const SPREADSHEET_PROFILE_PARTS_MAX = 8;
/**
 * How many parts the embedded-object inventory may be delivered in.
 *
 * Parted for the same reason a profile is: the number of objects is a property of the *file*, and
 * a sheet with more icons than one part holds must not be truncated into a shorter inventory than
 * the document has. `EMBEDDED_INVENTORY_PARTS_MAX` on the worker side is the same number.
 */
export const SPREADSHEET_EMBEDDED_INVENTORY_PARTS_MAX = 8;
/**
 * How many bytes of UTF-8 of a column's name part 0 of a profile keeps.
 *
 * Bytes rather than characters because bytes are what the part is bounded in — a name of
 * ideographs costs three times what its length suggests. A longer name is stored cut at a
 * character boundary with `nameTruncated` on its entry, and a lookup by name matches the same
 * head, so both the worker that cuts and the read that matches take the number from here.
 */
export const SPREADSHEET_PROFILE_HEADER_BYTES = 128;
/**
 * The vocabulary of `ProcessingProgress.stage` — what a run is doing right now, as a machine
 * label a surface maps to its own words.
 *
 * A union rather than a free string because both sides of the wire read it: the schema's
 * validator (`artifactProcessingLib/progress.ts`) is derived from this array, and the browser
 * maps it to a phrase.
 *
 * Not the job-row stage vocabulary (`DocumentProcessingStage`): a row's stage says which piece
 * of the pipeline owns it, this says what that piece is doing — one `parse_document` row reads
 * and then merges.
 */
export const PROCESSING_PROGRESS_STAGES = [
    "reading",
    "merging",
    "describing_figures",
    "generating_card",
    "transcribing",
];
/**
 * The vocabulary of `ProcessingProgress.unitLabel` — what `done` and `total` are counted in.
 *
 * `parts` is the unit of last resort: the run can place itself on no axis the file has, so it
 * counts the pieces of work it was cut into. `seconds` is the recording axis and is formatted as
 * a clock, never as a count.
 */
export const PROCESSING_PROGRESS_UNITS = [
    "pages",
    "slides",
    "sheets",
    "rows",
    "figures",
    "parts",
    "seconds",
];
/** The words a stage is announced in. */
const PROCESSING_PROGRESS_STAGE_VERBS = {
    reading: "Reading",
    merging: "Merging",
    describing_figures: "Describing pictures",
    generating_card: "Writing the summary",
    transcribing: "Transcribing",
};
/**
 * The noun a unit's counts are spoken with, where the unit has one.
 *
 * `sheets` is singular because the count names one worksheet at a time ("sheet 7 of 30"), while
 * a page count names a span of them. `seconds` carries none: a clock reads as a clock.
 */
const PROCESSING_PROGRESS_UNIT_NOUNS = {
    pages: "pages",
    slides: "slides",
    sheets: "sheet",
    rows: "rows",
    figures: "pictures",
    parts: "parts",
};
/**
 * Digits grouped the way every other count on these surfaces is, without a locale. Exported
 * because a producer spells the `current` fragment ("1,200–1,800") and the formatter spells the
 * denominator beside it, and two groupings inside one phrase read as two documents.
 */
export function formatProgressCount(value) {
    return Math.max(0, Math.round(value))
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
/** `0:45`, `12:30`, `1:02:05` — a recording's position, never a count of seconds. */
function formatProgressClock(seconds) {
    const whole = Math.max(0, Math.round(seconds));
    const hours = Math.floor(whole / 3600);
    const minutes = Math.floor((whole % 3600) / 60);
    const rest = whole % 60;
    const paddedSeconds = rest.toString().padStart(2, "0");
    if (hours === 0)
        return `${minutes}:${paddedSeconds}`;
    return `${hours}:${minutes.toString().padStart(2, "0")}:${paddedSeconds}`;
}
/**
 * The phrase a progress record reads as: "Reading pages 121–180 of 3,590", "Reading sheet 7 of
 * 30: Fact Data", "Describing pictures 12 of 40", "Transcribing 0:45 of 12:30".
 *
 * One function, in `@repo/shared`, because the Documents row and the platform admin table both
 * show it. Three rules decide the shape, each about not saying more than the record knows:
 *
 * - **A run with nothing to count is announced, not counted.** `total` of `0` has no denominator
 *   and a single `parts` denominator has no progress inside it, so both read as the bare verb.
 * - **`current` replaces the numerator where it names the same thing.** A page range is what is
 *   being read; a worksheet's name is a label on the numerator, not the numerator, so it is
 *   appended after the count instead.
 * - **The unit noun is dropped where the verb already carries it**, so a picture count reads
 *   "Describing pictures 12 of 40" rather than repeating the word.
 *
 * `verb: "omit"` leaves the stage out for a surface whose label already names it, so the same
 * record reads "12 of 40" or "pages 121–180 of 3,590" beside the step.
 */
export function formatProcessingProgress(progress, options = {}) {
    const verb = PROCESSING_PROGRESS_STAGE_VERBS[progress.stage];
    // A step label that already names the stage wants only the count beside it: "12 of 40",
    // "pages 121–180 of 3,590". The noun still follows the same rule, so it is never repeated
    // either.
    const lead = options.verb === "omit" ? "" : verb;
    const countable = progress.total > 0 && !(progress.unitLabel === "parts" && progress.total <= 1);
    if (!countable)
        return lead;
    const withLead = (rest) => (lead ? `${lead} ${rest}` : rest);
    if (progress.unitLabel === "seconds") {
        return withLead(`${formatProgressClock(progress.done)} of ${formatProgressClock(progress.total)}`);
    }
    const noun = PROCESSING_PROGRESS_UNIT_NOUNS[progress.unitLabel];
    const nounShown = noun && !verb.toLowerCase().endsWith(noun) ? noun : undefined;
    const withHead = (rest) => withLead(nounShown ? `${nounShown} ${rest}` : rest);
    const total = formatProgressCount(progress.total);
    if (progress.unitLabel === "sheets") {
        const count = `${formatProgressCount(progress.done)} of ${total}`;
        return withHead(progress.current ? `${count}: ${progress.current}` : count);
    }
    const numerator = progress.current ?? formatProgressCount(progress.done);
    return withHead(`${numerator} of ${total}`);
}
/**
 * Why a reader could not keep a picture, in the closed vocabulary the reader itself reports.
 *
 * - `too_small` — below the minimum pixel dimension a picture must have to carry content.
 * - `too_few_bytes` — below the minimum byte size, whatever its declared dimensions.
 * - `undecodable` — the bytes are in a format this reader cannot open (EMF, WMF, JPEG XR).
 * - `linked` — the document points at a file outside itself; there are no bytes to extract.
 * - `upload_failed` — the picture was read and the upload of it did not land.
 * - `byte_budget` — the shared byte pool for one document was already spent.
 * - `cap` — the per-document count cap or a request's upload allowance refused it.
 *
 * Only `cap` is repairable by asking for more; every other reason would refuse the same picture
 * on every re-read, which is what stops a lost picture from offering a futile "Read all
 * pictures".
 */
export const FIGURE_DROP_REASONS = [
    "too_small",
    "too_few_bytes",
    "undecodable",
    "linked",
    "upload_failed",
    "byte_budget",
    "cap",
];
/** What a clause says about one reason, once the count is in front of it. */
const FIGURE_DROP_REASON_CLAUSES = {
    too_small: "too small to carry content",
    too_few_bytes: "holding too few bytes to carry content",
    undecodable: "this reader cannot decode",
    linked: "linked to a file outside this document",
    upload_failed: "whose upload did not land",
    byte_budget: "past this document's picture byte budget",
    cap: "past this document's picture limit",
};
function pluralizeCount(count, singular, plural) {
    return `${count.toLocaleString("en-US")} ${count === 1 ? singular : plural}`;
}
function joinClauses(clauses) {
    if (clauses.length <= 1)
        return clauses[0] ?? "";
    if (clauses.length === 2)
        return `${clauses[0]} and ${clauses[1]}`;
    return `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
}
/**
 * The noun phrase for one reason's pictures: their formats where the reader named them, their
 * pixel size where a single picture is the whole group, and a plain count otherwise.
 *
 * Formats are counted rather than listed one by one, because a deck that lost forty icons of
 * one format must not produce forty clauses; a group whose formats the reader did not name
 * falls back to the bare count rather than inventing one.
 */
function figureDropSubject(count, details) {
    if (count === 1 && details.length === 1) {
        const only = details[0];
        const size = only.width !== undefined && only.height !== undefined
            ? `${only.width}×${only.height}-pixel `
            : "";
        const format = only.format ? `${only.format} ` : "";
        // The article form only where the phrase opens on the pixel size. Opening on a format name
        // would have the sentence choose between "a EMF image" and "an EMF image" — a judgement
        // about how an acronym is pronounced — and the counted form below says the same thing
        // without making it.
        if (size)
            return `a ${size}${format}image`;
    }
    const byFormat = new Map();
    for (const detail of details) {
        if (!detail.format)
            return pluralizeCount(count, "image", "images");
        byFormat.set(detail.format, (byFormat.get(detail.format) ?? 0) + 1);
    }
    if (byFormat.size === 0 || details.length !== count) {
        return pluralizeCount(count, "image", "images");
    }
    const parts = [...byFormat.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([format, formatCount]) => `${formatCount.toLocaleString("en-US")} ${format}`);
    return `${joinClauses(parts)} ${count === 1 ? "image" : "images"}`;
}
/**
 * The drop marker's one sentence: which pictures a document does not contain, and why.
 *
 * This is what the model reads — the `metadata` block `appendFigureDropMarker` appends to a
 * parsed document — and it is one sentence because a block in the middle of a document has room
 * for one. The Documents screen is **not** a caller: it renders its own per-reason sentences off
 * the facts (`figures.dropped`, `figures.decorative`), where a screen can afford a paragraph and
 * a repair. What the two share is the vocabulary above, not a body.
 *
 * Decorative drops are deliberately not in it: they were never content, and naming them here
 * would report a meeting export whose every real picture arrived as having lost hundreds.
 *
 * A parse that attributed nothing still gets a sentence — the count is a fact even when the
 * cause is not — and never invents a reason for it.
 */
export function describeFigureDrops(drops) {
    if (!drops || drops.total <= 0)
        return null;
    const head = pluralizeCount(drops.total, "picture", "pictures");
    const verb = drops.total === 1 ? "was" : "were";
    const detail = drops.detail ?? [];
    const clauses = FIGURE_DROP_REASONS.flatMap((reason) => {
        const count = drops.byReason[reason] ?? 0;
        if (count <= 0)
            return [];
        const subject = figureDropSubject(count, detail.filter((entry) => entry.reason === reason));
        return [`${subject} ${FIGURE_DROP_REASON_CLAUSES[reason]}`];
    });
    if (clauses.length === 0)
        return `${head} ${verb} left out.`;
    return `${head} ${verb} left out: ${joinClauses(clauses)}.`;
}
/**
 * What a screen says about the pictures a reader set aside as repeats, when somebody asks.
 *
 * Its own sentence and never part of the drop sentence: a repeated avatar is not a picture the
 * document lost, and folding it into the loss would offer a repair for something that is
 * working as intended.
 */
export function describeDecorativeFigures(count) {
    if (count === undefined || count <= 0)
        return null;
    const subject = pluralizeCount(count, "repeated picture", "repeated pictures");
    const verb = count === 1 ? "was" : "were";
    return `${subject} (a logo, an icon, a per-speaker avatar) ${verb} set aside as decoration.`;
}
/**
 * Which kind of degradation a free-text card reason describes, for rows that carry no recorded
 * kind — the one place that mapping exists, so a live write and a stored read cannot classify
 * the same sentence two ways.
 *
 * The two derived reasons are stamped verbatim by the card mutation and match their kinds by
 * name. A `deterministic_<kind>_card` marker is what the card action throws for a document that
 * is never sent to a model. Anything else non-empty is a model call that failed.
 */
export function deriveDocumentCardDegradedReasonKind(reason) {
    if (!reason)
        return undefined;
    if (reason === "partial_coverage")
        return "partial_coverage";
    if (reason === "empty_source_revision")
        return "empty_source_revision";
    if (/^deterministic_.+_card$/.test(reason))
        return "deterministic";
    return "generation_failed";
}
/** How many unread sheets a coverage names; the rest are counted in `notReadOmitted`. */
export const SHEETS_NOT_READ_MAX = 20;
/**
 * The sentences a reader shows for the sheets a workbook's reading left unread — one per reason,
 * each naming its sheets ("Chart sheet not read: Chart1 ADS vs UCS.") — and whether any of them
 * is a loss. A chart sheet holds a chart and no cells, so leaving it unread is a fact about the
 * file; any other reason is a sheet whose cells a reader does not hold.
 *
 * One function, because the Documents popover and Claudia's document tools disclose the same
 * sheets and must name them the same way.
 */
export function describeSheetsNotRead(coverage) {
    const byReason = new Map();
    for (const { name, reason } of coverage?.sheets?.notRead ?? []) {
        byReason.set(reason, [...(byReason.get(reason) ?? []), name]);
    }
    const sentences = [...byReason].map(([reason, names]) => `${reason === "chart_sheet" ? "Chart sheet" : "Sheet"}${names.length === 1 ? "" : "s"} not read` +
        `${reason === "window_incomplete" ? " to the end" : ""}: ${names.join(", ")}.`);
    const omitted = coverage?.sheets?.notReadOmitted ?? 0;
    if (omitted > 0)
        sentences.push(`${omitted} more sheet${omitted === 1 ? " was" : "s were"} not read.`);
    const loss = omitted > 0 || [...byReason.keys()].some((reason) => reason !== "chart_sheet");
    return { sentences, loss };
}
/**
 * A list of spans as a reader names them: "1–66", or "1–66, 100–105".
 *
 * One function, because a coverage that reaches four surfaces has to be spelled the same way on
 * all of them. `formatNumber` is how a surface that separates thousands gets its own digits
 * without owning the join.
 */
export function formatPageRangeList(ranges, formatNumber = String) {
    return ranges
        .map((range) => `${formatNumber(range.start)}–${formatNumber(range.end)}`)
        .join(", ");
}
/** Pages a set of spans holds, counting each span's own ends. */
export function countPageRanges(ranges) {
    return ranges.reduce((sum, range) => sum + Math.max(0, range.end - range.start + 1), 0);
}
/**
 * The sentence a reader shows — a person or a model — when a revision holds less than the whole
 * file, or `null` when it holds all of it.
 *
 * One function, in `@repo/shared`, because three surfaces disclose the same fact and must not
 * disagree: Claudia's document tools, the card's degradation reason, and the Documents row's
 * completeness read-out. Absent coverage reads as complete.
 *
 * **It names every span, never the distance between the first and the last.** A document read
 * twice — pages 1–66, then 100–105 — has a hole in it, and a sentence saying "pages 1–105" would
 * promise 39 pages nobody converted. The spans are also what lets a reader decide what to read
 * next, which is the only reason to disclose the gap at all.
 */
export function describeDocumentCoverage(coverage) {
    if (!coverage || coverage.status !== "partial")
        return null;
    const ranges = coverage.coveredPageRanges ?? [];
    const total = coverage.totalPages;
    const spans = formatPageRangeList(ranges);
    if (spans && total !== undefined && total > 0) {
        const unread = Math.max(0, total - countPageRanges(ranges));
        return `Pages ${spans} of ${total} were read; ${unread} were not.`;
    }
    if (spans)
        return `Pages ${spans} were read; the rest of the file was not.`;
    return "Only part of this file was read; the rest was not.";
}
/**
 * A page, slide or sheet count as a *present* fact — `undefined` for a stored `0`.
 *
 * These spans are 1-indexed, so zero is never one of them: a stored `0` can only be a reader
 * that defaulted an absence instead of omitting it, and a popover reading it out says "All 0
 * pages were read." Every read path passes a stored span through here, because a fix at the
 * write end cannot reach a number already recorded.
 */
export function presentSpanCount(count) {
    return count === undefined || count <= 0 ? undefined : count;
}
/**
 * What the blocks behind a processing revision *are*: ordinary read text, or a conversation in
 * turns. Shared because both sides decide from it — the backend refuses to serve the transcript
 * viewer anything that is not a transcript, and the browser only offers to open one.
 *
 * Two values and no third: `transcript` claims a grammar, that every paragraph block is a turn
 * whose first line `parseTranscriptTurnHeader` reads. Anything else is `prose`.
 */
/**
 * Where a document's page numbers come from, or that it has none.
 *
 * - `printed` — the file itself states the page a thing sits on. A PDF is the only format that
 *   does; the number is the one a reader would print.
 * - `rendered` — the pages Word last rendered, recorded in the file as the page-break markers the
 *   producer wrote. The numbers are real and citable, and they are the producer's pagination
 *   rather than a renderer's re-flow of it.
 * - `laid_out` — the pages this file lays out on, assigned from a layout the reader performed
 *   itself because the file's markers disagreed with the page count it declares. A boundary can
 *   differ from Word's own copy by a page, where a font is substituted.
 * - `none` — the file states no pages: the format has no page axis at all, or this reader found
 *   none. A workbook is read by worksheet and a deck by slide; a Word file whose producer wrote
 *   no break markers has nothing to number.
 *
 * It is a property of one *reading* rather than of the file: a document read by a reader that
 * finds no markers has no pages until a reader that finds them reads it again. That is why it is
 * stored on the revision beside `pageCount` and never on the artifact.
 */
export const DOCUMENT_PAGE_AXES = ["printed", "rendered", "laid_out", "none"];
export const ARTIFACT_CONTENT_SHAPES = ["prose", "transcript"];
/**
 * Whether this document holds pictures whose stored images are gone — the answer the Reprocess
 * button and `projectDocuments.reprocessDocuments` both read, so the two cannot disagree.
 *
 * Derived rather than carried as a field of its own: `completeness.descriptions.reasonKind` is
 * already on the row, so a second flag beside it would be a second thing to keep true.
 *
 * The repair is a full re-read and only a full re-read. "Read more…" resumes converted parts
 * from storage exactly as they are, so it re-creates the same figure rows pointing at the same
 * missing images; only a run that converts the file again uploads the pictures afresh.
 */
export function isDocumentPicturesUnavailable(processing) {
    return processing?.completeness?.descriptions?.reasonKind === "image_unavailable";
}
/**
 * The first bite the pre-read question suggests for a page or slide range. A suggestion, not a
 * rule — every caller clamps it to what the file holds, so a 412-slide deck proposes all 412
 * rather than a range that does not exist.
 */
export const PRE_READ_SUGGESTED_SPAN = 500;
/**
 * How many *new* pages a widening may convert before the person is asked whether they want all
 * of it. Half the span the pre-read question suggests as a first bite: the two are a ratio, not
 * two numbers, so raising what a person is offered raises what a model may take without asking.
 *
 * Measured on the pages a request would actually convert — the union with what was read, less
 * what was read — never on the size of the file.
 */
export const DOCUMENT_READ_MORE_CONFIRM_PAGES = PRE_READ_SUGGESTED_SPAN / 2;
/**
 * How many page ranges, or worksheet names, one `document_read_more` request may name.
 *
 * Every piece is carried through the plan, the effective scope, the confirmation question and
 * the label that summarizes it, so the count is bounded rather than left to whatever a model
 * writes. Sixty-four is well past any reading somebody would recognise as one span; a request
 * that needs more is one whose adjacent ranges were never merged.
 */
export const DOCUMENT_READ_MORE_MAX_PARTS = 64;
/**
 * The scope every artifact is read with until someone narrows it.
 *
 * `everything` is representable rather than spelled "absent": a stored value says "this file's
 * scope was decided, and the decision was the whole file", which is a different fact from a file
 * nobody has been asked about.
 */
export const EVERYTHING_READ_SCOPE = { kind: "everything" };
/**
 * The scope an artifact is read with — its own, or the default for one that has none.
 *
 * One function, so no call site has to remember which way absence falls: a caller that defaulted
 * the other way would ask the worker for nothing.
 */
export function resolveArtifactReadScope(artifact) {
    return artifact.readScope ?? EVERYTHING_READ_SCOPE;
}
/**
 * Whether anybody has decided how much of this file to read.
 *
 * The one question `resolveArtifactReadScope` cannot answer, and the only one the large-lane
 * dispatcher may ask: absence means the pre-read question is unanswered (so a large document
 * parks after its probe), while a stored `{kind:"everything"}` means it *was* asked. Every other
 * caller wants the value, not the provenance.
 */
export function hasAnsweredReadScope(artifact) {
    return artifact.readScope !== undefined;
}
/**
 * The exact set of page or slide ranges a `pages` scope selects.
 *
 * The one accessor, so no caller has to remember that `ranges` is absent for the ordinary
 * single-range scope — reading `scope.ranges ?? []` would plan nothing at all for those.
 */
export function readScopePageRanges(scope) {
    const ranges = scope.ranges;
    if (!ranges || ranges.length === 0)
        return [{ start: scope.start, end: scope.end }];
    return ranges.map((range) => ({ start: range.start, end: range.end }));
}
/**
 * A `pages` scope built from any set of ranges — sorted, merged, and spelled the short way
 * whenever it can be.
 *
 * Merging *adjacent* ranges and not only overlapping ones is what keeps "read pages 1–60, then
 * pages 61–120" from being a different document identity than "read pages 1–120": the same set
 * of pages has to be the same scope. It is also what makes the resumable chunk prefix work — the
 * merged span re-splits at the same chunk boundaries the earlier run obeyed.
 *
 * The single-range result deliberately omits `ranges`, which is what keeps its token and its
 * revision key identical to a scope stored without the field.
 */
export function pagesReadScope(ranges) {
    const merged = mergePageRanges(ranges);
    const first = merged[0];
    const last = merged[merged.length - 1];
    if (!first || !last)
        return null;
    if (merged.length === 1)
        return { kind: "pages", start: first.start, end: first.end };
    return { kind: "pages", start: first.start, end: last.end, ranges: merged };
}
/**
 * Sorted, de-duplicated, overlapping-or-touching spans joined into one.
 *
 * Ranges that are not whole positive numbers, or that end before they start, are dropped rather
 * than repaired: they describe no pages, and inventing a boundary for them would read a part of
 * the file nobody asked for.
 */
export function mergePageRanges(ranges) {
    const usable = ranges
        .filter((range) => Number.isSafeInteger(range.start) &&
        Number.isSafeInteger(range.end) &&
        range.start >= 1 &&
        range.end >= range.start)
        .sort((left, right) => left.start - right.start || left.end - right.end);
    const merged = [];
    for (const range of usable) {
        const previous = merged[merged.length - 1];
        // `+ 1` is the touching case: 1–55 and 56–110 are one span of pages, not two.
        if (previous && range.start <= previous.end + 1) {
            previous.end = Math.max(previous.end, range.end);
            continue;
        }
        merged.push({ start: range.start, end: range.end });
    }
    return merged;
}
/**
 * The scope that covers both of two scopes — what "read more of this" actually asks for.
 *
 * Never a replacement for the scope a document already has: a new scope that dropped the pages
 * already read would throw away a conversion somebody waited for. So the stored scope becomes
 * the union, and the plan derived from it covers the old span and the new one together.
 *
 * The two degenerate combinations both widen rather than narrow, because a union that lost part
 * of either operand would be the one outcome nobody could have asked for:
 *
 * - **`text_only` with anything else.** Depth and span are different axes and no scope can say
 *   "these pages with pictures, the rest without", so the other operand wins — it is the one
 *   that reads *more*. Unreachable through the row: `text_only` is never scope-limited.
 * - **`pages` with `sheets`.** Two axes no single scope can hold at once, so the union is the
 *   whole file. Unreachable through the row too: a file has one axis, not both.
 */
export function unionReadScopes(left, right) {
    if (left.kind === "everything" || right.kind === "everything")
        return EVERYTHING_READ_SCOPE;
    if (left.kind === "text_only")
        return right;
    if (right.kind === "text_only")
        return left;
    if (left.kind === "pages" && right.kind === "pages") {
        return (pagesReadScope([...readScopePageRanges(left), ...readScopePageRanges(right)]) ??
            EVERYTHING_READ_SCOPE);
    }
    if (left.kind === "sheets" && right.kind === "sheets") {
        return { kind: "sheets", names: sortedUniqueSheetNames([...left.names, ...right.names]) };
    }
    return EVERYTHING_READ_SCOPE;
}
/** Prefix of every read-scope capability token, so one `startsWith` can spot them all. */
export const READ_SCOPE_CAPABILITY_PREFIX = "read-scope:";
/**
 * The capability token a scope contributes to a parse's identity — or `null` for the scope that
 * contributes none.
 *
 * **`everything` deliberately has no token.** The token joins `processingCapabilities`, which is
 * hashed into every revision key and idempotency key, so a token for the default would change
 * the identity of every document ever parsed and mark a whole corpus outdated. The rule: an
 * unnarrowed scope contributes nothing, and every narrowing contributes exactly one token.
 *
 * The sheet token hashes the names rather than listing them, for two reasons. A token is joined
 * into a hash input, so an unbounded list of user-authored worksheet names would put arbitrary
 * length — and arbitrary separator characters — inside a key's segment structure. And the hash
 * is taken over the *sorted, de-duplicated* names, which is what makes the token
 * order-independent: two people who pick the same three sheets in different orders get one
 * revision, not two parses of identical content.
 *
 * A `pages` token lists every range it holds, comma-separated and in order
 * (`read-scope:pages:1-55,200-260`), so a single-range scope produces exactly
 * `read-scope:pages:1-55` and no stored revision key moves.
 */
export function readScopeToken(scope) {
    switch (scope.kind) {
        case "everything":
            return null;
        case "pages":
            return `${READ_SCOPE_CAPABILITY_PREFIX}pages:${readScopePageRanges(scope)
                .map((range) => `${range.start}-${range.end}`)
                .join(",")}`;
        case "sheets":
            return `${READ_SCOPE_CAPABILITY_PREFIX}sheets:${stableReadScopeHash(sortedUniqueSheetNames(scope.names).join(SHEET_NAME_SEPARATOR))}`;
        case "text_only":
            return `${READ_SCOPE_CAPABILITY_PREFIX}text-only`;
    }
}
/**
 * The names a `sheets` scope actually selects: de-duplicated, in one canonical order. Exported
 * because the token is not the only thing that has to be order-blind — a caller comparing two
 * scopes must agree with the hash about what "the same selection" means.
 */
export function sortedUniqueSheetNames(names) {
    return [...new Set(names)].sort();
}
/**
 * The separator the sheet-name hash joins on: a character no spreadsheet application allows in a
 * worksheet name, so `["A", "B"]` and `["A B"]` can never hash alike.
 */
const SHEET_NAME_SEPARATOR = "\u0000";
/**
 * FNV-1a over UTF-8 bytes, 64 bits, hex. Not a security primitive and not a content hash: its
 * whole job is to turn an arbitrary-length list of names into a fixed, separator-free segment
 * that is byte-identical on every runtime that computes it — the browser deciding what to ask
 * for, and the backend deciding what that request is worth reusing.
 */
function stableReadScopeHash(value) {
    const bytes = new TextEncoder().encode(value);
    let hash = 0xcbf29ce484222325n;
    for (const byte of bytes) {
        hash = BigInt.asUintN(64, (hash ^ BigInt(byte)) * 0x100000001b3n);
    }
    return hash.toString(16).padStart(16, "0");
}
